import { useAuth } from '@/hooks/use-auth';
import { useProfile } from '@/hooks/useProfile';
import { buildInviteLink } from '@/lib/invite-links';
import { invalidateGroceryListsCache } from '@/lib/grocery-list-cache';
import { removeCachedProfile } from '@/lib/profile-cache';
import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useState } from 'react';

/** Household row from Supabase */
export interface Household {
  id: string;
  name: string;
  created_by_user_id: string;
  max_members: number;
  created_at: string;
}

/** Minimal profile for household members list */
export interface HouseholdMember {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  household_id: string | null;
}

/** Invite row (for listing or after create) */
export interface HouseholdInvite {
  id: string;
  household_id: string;
  invite_token: string;
  created_by_user_id: string;
  expires_at: string | null;
  created_at: string;
}

const householdCache: Record<string, { data: Household; timestamp: number }> = {};
const CACHE_TTL_MS = 60_000;

function getCachedHousehold(householdId: string): Household | null {
  const entry = householdCache[householdId];
  if (!entry || Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
  return entry.data;
}

function setCachedHousehold(h: Household): void {
  householdCache[h.id] = { data: h, timestamp: Date.now() };
}

function invalidateHouseholdCache(householdId: string): void {
  delete householdCache[householdId];
}

/** Extract a readable message from Supabase/unknown errors (avoids "[object Object]"). */
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  const msg =
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
      ? (err as { message: string }).message
      : String(err);
  return new Error(msg);
}

/** Generate a URL-safe invite token */
function generateInviteToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Fetch the current user's household (from profile.household_id).
 * Uses a short-lived cache. Returns null if user has no household.
 */
export function useHousehold() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const householdId = profile?.household_id ?? null;
  const cached = householdId ? getCachedHousehold(householdId) : null;
  const [household, setHousehold] = useState<Household | null>(cached);
  const [loading, setLoading] = useState(!cached && !!householdId);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!user?.id || !householdId) {
      setHousehold(null);
      setLoading(false);
      return;
    }
    const cachedH = getCachedHousehold(householdId);
    if (cachedH) {
      setHousehold(cachedH);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('households')
        .select('*')
        .eq('id', householdId)
        .single();
      if (fetchError) throw fetchError;
      if (data) {
        const h = data as Household;
        setHousehold(h);
        setCachedHousehold(h);
      } else {
        setHousehold(null);
      }
    } catch (err) {
      setError(toError(err));
      setHousehold(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, householdId]);

  useEffect(() => {
    if (!householdId) {
      setHousehold(null);
      setLoading(false);
      return;
    }
    const cachedH = getCachedHousehold(householdId);
    if (cachedH) {
      setHousehold(cachedH);
      setLoading(false);
      return;
    }
    refetch();
  }, [householdId, refetch]);

  const displayHousehold = householdId ? (getCachedHousehold(householdId) ?? household) : null;
  const displayLoading = householdId ? !displayHousehold && loading : false;

  const updateHouseholdName = useCallback(
    async (name: string): Promise<Household | null> => {
      const trimmed = name.trim();
      if (!user?.id || !householdId || !trimmed) return null;
      const current = getCachedHousehold(householdId) ?? household;
      if (!current || current.name === trimmed) return current;
      setError(null);
      try {
        const { data, error: updateError } = await supabase
          .from('households')
          .update({ name: trimmed })
          .eq('id', householdId)
          .select()
          .single();
        if (updateError) throw updateError;
        const updated = data as Household;
        setHousehold(updated);
        setCachedHousehold(updated);
        return updated;
      } catch (err) {
        const e = toError(err);
        setError(e);
        throw e;
      }
    },
    [user?.id, householdId, household]
  );

  return {
    household: displayHousehold,
    loading: displayLoading,
    error,
    refreshHousehold: refetch,
    updateHouseholdName,
  };
}

/**
 * Create a new household and set it as the current user's household.
 * Fails if the user already has a household_id (leave first).
 * On success, profile cache is invalidated so call refreshProfile() to update UI.
 */
export function useCreateHousehold() {
  const { user } = useAuth();
  const { profile, refreshProfile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createHousehold = useCallback(
    async (name: string): Promise<Household | null> => {
      if (!user?.id) {
        setError(new Error('You must be signed in to create a household'));
        return null;
      }
      if (profile?.household_id) {
        setError(new Error('You already belong to a household. Leave it first to create a new one.'));
        return null;
      }
      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('create_household_for_user', {
          household_name: name.trim() || 'My Household',
        } as any);
        if (rpcError) throw rpcError;
        const newHousehold = data as Household;

        setCachedHousehold(newHousehold);
        removeCachedProfile(user.id);
        await refreshProfile();
        return newHousehold;
      } catch (err) {
        const e = toError(err);
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id, profile?.household_id, refreshProfile]
  );

  return { createHousehold, loading, error };
}

/**
 * Join a household using an invite token (e.g. from deep link or manual entry).
 * On success, profile cache is invalidated; call refreshProfile() if needed.
 * Enforces max_members before updating (and DB trigger enforces as well).
 */
export function useJoinHousehold() {
  const { user } = useAuth();
  const { profile, refreshProfile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const joinHousehold = useCallback(
    async (inviteToken: string): Promise<Household | null> => {
      if (!user?.id) {
        setError(new Error('You must be signed in to join a household'));
        return null;
      }
      const token = inviteToken.trim();
      if (!token) {
        setError(new Error('Invalid invite token'));
        return null;
      }
      setLoading(true);
      setError(null);
      try {
        const { data: householdId, error: rpcError } = await supabase.rpc('get_household_id_for_invite_token', {
          token,
        });
        if (rpcError) throw rpcError;
        if (!householdId) {
          setError(new Error('Invalid or expired invite link'));
          return null;
        }

        // Skip capacity check if already in this household (e.g. re-joining)
        if (profile?.household_id !== householdId) {
          const { data: householdRow, error: hErr } = await supabase
            .from('households')
            .select('max_members, name')
            .eq('id', householdId)
            .maybeSingle();
          if (hErr) throw hErr;
          if (!householdRow) {
            setError(new Error('Invalid or expired invite link'));
            return null;
          }
          const maxMembers = householdRow.max_members ?? 2;
          const { count, error: countErr } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('household_id', householdId);
          if (countErr) throw countErr;
          if ((count ?? 0) >= maxMembers) {
            const msg = `This household has reached its maximum number of members (${maxMembers}).`;
            setError(new Error(msg));
            throw new Error(msg);
          }
        }

        const { error: updateError } = await supabase
          .from('profiles')
          .update({ household_id: householdId, updated_at: new Date().toISOString() })
          .eq('id', user.id);
        if (updateError) throw updateError;

        removeCachedProfile(user.id);
        await refreshProfile();

        const { data: householdData, error: fetchError } = await supabase
          .from('households')
          .select('*')
          .eq('id', householdId)
          .maybeSingle();
        if (fetchError) throw fetchError;
        if (householdData) {
          const h = householdData as Household;
          setCachedHousehold(h);
          return h;
        }
        return null;
      } catch (err) {
        const e = toError(err);
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id, profile?.household_id, refreshProfile]
  );

  return { joinHousehold, loading, error };
}

/**
 * Leave the current household (sets profile.household_id to null).
 * On success, profile cache is invalidated; call refreshProfile() to update UI.
 */
export function useLeaveHousehold() {
  const { user } = useAuth();
  const { profile, refreshProfile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const leaveHousehold = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setError(new Error('You must be signed in to leave a household'));
      return false;
    }
    if (!profile?.household_id) {
      setError(new Error('You are not in a household'));
      return false;
    }
    const previousHouseholdId = profile.household_id;
    setLoading(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ household_id: null, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (updateError) throw updateError;

      invalidateHouseholdCache(previousHouseholdId);
      invalidateGroceryListsCache();
      removeCachedProfile(user.id);
      await refreshProfile();
      return true;
    } catch (err) {
      const e = toError(err);
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [user?.id, profile?.household_id, refreshProfile]);

  return { leaveHousehold, loading, error };
}

/**
 * Generate an invite for the current household and return the token/link.
 * Requires the household_invites table and RLS (see migration 20250206000000_household_invites.sql).
 */
export function useInviteMember() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const inviteMember = useCallback(
    async (options?: { expiresInDays?: number }): Promise<{ token: string; link: string } | null> => {
      if (!user?.id) {
        const e = new Error('You must be signed in to invite members');
        setError(e);
        throw e;
      }
      const householdId = profile?.household_id;
      if (!householdId) {
        const e = new Error('You must belong to a household to invite members');
        setError(e);
        throw e;
      }
      setLoading(true);
      setError(null);
      try {
        const { data: householdRow, error: hErr } = await supabase
          .from('households')
          .select('max_members, name')
          .eq('id', householdId)
          .maybeSingle();
        if (hErr) throw hErr;
        if (!householdRow) {
          const e = new Error('Household not found. You may have been removed.');
          setError(e);
          throw e;
        }
        const maxMembers = householdRow.max_members ?? 2;
        const { count, error: countErr } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('household_id', householdId);
        if (countErr) throw countErr;
        if ((count ?? 0) >= maxMembers) {
          const msg = `Your household has reached its maximum number of members (${maxMembers}). No new invites can be created.`;
          setError(new Error(msg));
          throw new Error(msg);
        }

        const token = generateInviteToken();
        let expires_at: string | null = null;
        if (options?.expiresInDays != null && options.expiresInDays > 0) {
          const d = new Date();
          d.setDate(d.getDate() + options.expiresInDays);
          expires_at = d.toISOString();
        }
        const { error: insertError } = await supabase.from('household_invites').insert({
          household_id: householdId,
          invite_token: token,
          created_by_user_id: user.id,
          expires_at,
        });
        if (insertError) throw insertError;
        const link = buildInviteLink(token);
        return { token, link };
      } catch (err) {
        const e = toError(err);
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id, profile?.household_id]
  );

  return { inviteMember, loading, error };
}

/**
 * Fetch members of the current user's household (profiles with same household_id).
 * Requires RLS policy "Users can view profiles in same household".
 */
export function useHouseholdMembers() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const householdId = profile?.household_id ?? null;
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [loading, setLoading] = useState(!!householdId);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!user?.id || !householdId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, email, display_name, avatar_url, household_id')
        .eq('household_id', householdId)
        .order('display_name', { ascending: true, nullsFirst: false });
      if (fetchError) throw fetchError;
      setMembers((data ?? []) as HouseholdMember[]);
    } catch (err) {
      setError(toError(err));
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, householdId]);

  useEffect(() => {
    if (!householdId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    refetch();
  }, [householdId, refetch]);

  return { members, loading, error, refreshMembers: refetch };
}
